import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, share, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LogCategory, LoggerService, LogService, LogType } from '../core/services/logger.service';
import { PageType } from '../core/services/page-guard.service';
import { ProfileService } from '../core/services/profile.service';
import { TagManagerService } from '../core/services/tag-manager.service';
import { UtilityService } from '../core/services/utility.service';
import { EnrollmentsService } from './enrollments.service';

const CACHE_SIZE = 1;

@Injectable()
export class CourseCatalogService {
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
        })
    };
    private _apiUrl = environment.apiUrl + '/catalog';    
    private _lookups$: Observable<any>;
    

    public favorites$ = new BehaviorSubject([]);
    public waitlistHelpExpand = true;
    public sortCoursesByName = true;
    public updatedBids: number[] = [];
    public tabToShow: string;
    public defaultProgram: string = '';

    private storage: any;

    //  Used for the new course typeahead.  Available parameters: searchString (required), excludeGroups.
    //  query (URLSearchParams) required.
    public courseTypeaheadList$(query: URLSearchParams): Observable<any[]> {
        if (!query)
            return of([]);

        return this.http.get(`${environment.apiUrl}/catalog/courses/typeahead?${query}`).pipe(
            //tap(val => console.log('╔═ courseTypeaheadList$', val)),
            map((courses: any) => {
                return courses.map(item => ({
                    name: item.DisplayNameStudents, // required name
                    id: item.CourseId,              // required id
                    desc: item.Program
                }));
            }),
            shareReplay(1),
        );
    }

    public sectionDropdownList$(query: string): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/catalog/sections?${query}`).pipe(
            //tap(val => console.log('╔═ sectionDropdownList$', val)),
            shareReplay(1)
        );
    }

    constructor(
        private http: HttpClient,
        private profile: ProfileService,
        private logger: LoggerService,
        private gtm: TagManagerService,
        private utility: UtilityService,
        private enrollmentsService: EnrollmentsService
    ) {
        this.log("constructor", null, LogCategory.Constructor);
        var thisPage;
        //Get this page from the appropriate store and load the data into an observable object       
        if (!!this.profile.pageStore[PageType.Catalog]) {
            thisPage = this.profile.pageStore[PageType.Catalog];
            this.storage = this.profile.pageStore;
        } else {
            this.storage = this.profile.componentStore;
            thisPage = this.profile.componentStore[PageType.Catalog];
        }
       
        if (thisPage.Data.Favorites) {
            this.favorites$.next(thisPage.Data.Favorites.split(','));
        } else {
            this.favorites$.next([]);
        }

        if (thisPage.Data.WaitlistHelpExpand && thisPage.Data.WaitlistHelpExpand == "false")
            this.waitlistHelpExpand = false;

        if (thisPage.Data.SortCoursesByName && thisPage.Data.SortCoursesByName == "false")
            this.sortCoursesByName = false;

        if (thisPage.Data.DefaultProgram)
            this.defaultProgram = thisPage.Data.DefaultProgram;
     }
        
    
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.CourseCatalog, category);
    }

    public updateFavorites(data) {
        this.favorites$.next(data);
        this.storage[PageType.Catalog].Data.Favorites = data.join();
        this.profile.updatePagePreferences(PageType.Catalog, this.storage[PageType.Catalog]);
    }

    public updateWaitlistHelpExpand(data) {
        this.waitlistHelpExpand = data;
        this.storage[PageType.Catalog].Data.WaitlistHelpExpand = data;
        this.profile.updatePagePreferences(PageType.Catalog, this.storage[PageType.Catalog]);
    }

    public updateDismissWaitlistAlert(data) {
        var dismissed = [];
        if (this.storage[PageType.Catalog].Data.DismissedWaitlistAlert)
            this.storage[PageType.Catalog].Data.DismissedWaitlistAlert
                .split(",")
                .map(str => { return Number(str) });
        dismissed = [...new Set(dismissed.concat(data))];

        this.storage[PageType.Catalog].Data.DismissedWaitlistAlert = dismissed.toString();
        this.profile.updatePagePreferences(PageType.Catalog, this.storage[PageType.Catalog]);
        this.enrollmentsService.updateDismissed$.next(dismissed);
    }

    public updateSortCoursesByName(data) {
        this.sortCoursesByName = data;
        this.storage[PageType.Catalog].Data.SortCoursesByName = data;
        this.profile.updatePagePreferences(PageType.Catalog, this.storage[PageType.Catalog]);
    }

    public updateDefaultProgram(data) {
        this.defaultProgram = data;
        this.storage[PageType.Catalog].Data.DefaultProgram = data;
        this.profile.updatePagePreferences(PageType.Catalog, this.storage[PageType.Catalog]);
    }

    public catalog$(PageNumber, PageSize, IsBidding, IsRegistration, Filters: any) {        
        
        const query = {
            PageNumber,
            PageSize,
            SortCoursesByName: this.sortCoursesByName,
            IsBidding,
            IsRegistration,
            Filters
        };
        
        return this.http.post<any>(this._apiUrl, query, this.httpOptions)
            .pipe(
                tap((data) => {
                    this.log('catalog$', 'fetched catalog via api', LogCategory.SubscriptionData);
                }),
                shareReplay(CACHE_SIZE)
            );    
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {

            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to the console instead

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

    private generateSyllabus(sectionId) {
        const url = `${this._apiUrl}/${sectionId}/syllabus`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
            share(),
            map(
                (res: HttpResponse<Blob>) => {                    
                    return {
                        filename: res.headers.get('Content-Disposition'),
                        body: new Blob([res.body], { type: 'application/pdf' })
                    };
                })
        );
    }
      
    public downloadSyllabus(section) {
        this.gtm.gaDownload('Syllabus');
        section.isSyllabusDownloading = true;    
        this.generateSyllabus(section.SectionId).subscribe(
            (res:any) => {               
                let matches = /filename="(.*?)"/g.exec(res.filename);
                var filename = matches && matches.length > 1 ? matches[1] : "syllabus";
                var url = window.URL.createObjectURL(res.body);
                section.isSyllabusDownloading = false;
                //  Blobs do not have file names.  This allows for an automatic download of the file while
                //  retaining the original file name.
                this.utility.downloadFile(`${filename}`, url);
            }
        )
    }

}
